import axios from 'axios';

/**
 * Assistant Service
 *
 * Wraps the workspace AI chat backend at `/ai/chat` (v1).
 *
 * Conversation is stateless on the wire — every request sends the full
 * message history (capped server-side at 30) and the backend handles the
 * tool-calling loop internally before returning a final `reply`.
 *
 * Write operations are returned as `pending_actions` that must be
 * explicitly confirmed by the user via {@link confirmAction}.
 */

export const assistantService = {
  /**
   * Send the conversation and receive an assistant reply + any pending
   * write-actions awaiting confirmation + structured read-tool outputs.
   *
   * @param {Array<{ role: 'user'|'assistant', content: string }>} messages
   * @returns {Promise<{
   *   id: string,
   *   role: 'assistant',
   *   content: string,                              // backend `reply`
   *   pending_actions: Array<{
   *     id: string,
   *     tool: string,
   *     summary: string,
   *     preview: object,
   *     expires_at: string,
   *   }>,
   *   tool_results: Array<{
   *     tool: string,                               // e.g. 'get_outstanding_invoices'
   *     tool_call_id: string,
   *     data: object,                               // raw tool payload (or { _truncated: true } when too large)
   *   }>,
   *   created_at: string,
   * }>}
   * @throws {AssistantError} normalized error with `code` field for UI handling
   */
  async sendMessage(messages) {
    try {
      const response = await axios.post('/ai/chat', { messages });
      const data = response.data || {};
      return {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: data.reply || '',
        pending_actions: Array.isArray(data.pending_actions) ? data.pending_actions : [],
        tool_results: Array.isArray(data.tool_results) ? data.tool_results : [],
        created_at: new Date().toISOString(),
      };
    } catch (error) {
      throw normalizeError(error, 'send_failed');
    }
  },

  /**
   * Confirm a pending write-action returned from a previous reply.
   * The action id has a 15-minute TTL on the backend (Redis).
   *
   * @param {string} actionId
   * @returns {Promise<{ ok: true, result: object }>}
   * @throws {AssistantError} `code: 'action_expired'` for 410 responses
   */
  async confirmAction(actionId) {
    try {
      const response = await axios.post(`/ai/chat/actions/${actionId}/confirm`);
      return response.data;
    } catch (error) {
      throw normalizeError(error, 'confirm_failed');
    }
  },
};

// ============================================================
// Error normalization
// ============================================================

export class AssistantError extends Error {
  constructor(code, message, status) {
    super(message);
    this.name = 'AssistantError';
    this.code = code;
    this.status = status;
  }
}

function normalizeError(error, fallbackCode) {
  const status = error?.response?.status;
  const body = error?.response?.data || {};
  const backendCode = body.error || body.code;

  if (status === 429 || backendCode === 'AI_CHAT_RATE_LIMIT_EXCEEDED') {
    return new AssistantError('rate_limited', 'Te veel berichten in korte tijd', 429);
  }
  if (status === 410 || backendCode === 'action_expired') {
    return new AssistantError('action_expired', 'Deze actie is verlopen', 410);
  }
  if (status === 503) {
    return new AssistantError('service_unavailable', 'AI-service is niet geconfigureerd', 503);
  }
  if (status === 502) {
    return new AssistantError('upstream_error', 'AI-service tijdelijk niet bereikbaar', 502);
  }
  if (status === 400) {
    return new AssistantError('validation_error', body.message || 'Ongeldige aanvraag', 400);
  }
  return new AssistantError(
    fallbackCode,
    error?.message || 'Er ging iets mis',
    status
  );
}

export default assistantService;
